/**
 * The settings an app declares for the one settings list (I-2510): every
 * setting of the account config path, described where it is used —
 * in the app — instead of in a central list. An app's `@<app>/connect`
 * module hands its sections over (`ConnectAppModule.settingsSections`), the
 * shell collects every app's and renders them (`ConnectSettingsList`); a new
 * setting is a field declared next to the code that reads it, and the list
 * follows on its own.
 *
 * A field names its config key (`table`, `mail.display`), where it sits in
 * the key's document (`path`), how it is edited (`control`), the value that
 * applies while no layer sets it (`default`) and its texts — i18n keys of the
 * app's own message table (Connect: one table per app, I-2284), a title and a
 * description that says what the setting does.
 *
 * Master stores an app key's documents without knowing their fields (its
 * platform layer is `{}`): the declared defaults are the only defaults an
 * app key has. Every reader of such a key merges them under what master
 * answers — `useDeclaredAccountConfig(sections, key)` (`declaredConfig.ts`) —
 * so the list and the app show the same value.
 *
 * Pure functions, no Vue: node-env code and tests import them through
 * `@blpde/connect/config`.
 */
import { connectComboIsGlobalSearch } from "./keybindingCombo";
import { cloneJson, isJsonObject, type JsonObject } from "./mergePatch";
import type { DriveFolderPermission } from "../drive/types";

/**
 * How a setting is edited: a switch, a segmented choice of a few short
 * options, a select of more (or numeric options), a number, a text, one of
 * the appearances the layer may select, a keybinding (presets and a recorder
 * for a combo of one's own), or the options in an order the person chooses.
 */
export type ConnectSettingsControl =
  | "switch"
  | "choice"
  | "select"
  | "number"
  | "text"
  | "drive-folder"
  | "appearance"
  | "keybinding"
  /**
   * The options in an order the person chooses (a sortable list); stored as
   * the option values joined by `,` — one scalar string, so defaults, layers,
   * locks and designs treat it like any other value. Read it through
   * `connectSettingsOrder`.
   */
  | "order";

/** A value a setting stores. */
export type ConnectSettingsValue = string | number | boolean;

/** One option of a `choice`, a `select`, an `order` or the presets of a `keybinding`. */
export interface ConnectSettingsOption {
  /**
   * The stored value. `null` is the option of no value (e.g. „Je Tabelle"):
   * picking it removes the layer's own value. An `order` option has a string
   * value without `,`.
   */
  value: string | number | null;
  /** i18n key of the option's caption. */
  labelKey?: string;
  /** Params of `labelKey` (`{ n: 3 }` for „{n} Zeilen"). */
  labelParams?: Record<string, unknown>;
  /** A caption every language writes alike (`06:00`) — instead of `labelKey`. */
  label?: string;
}

/** A sub-heading inside a section. */
export interface ConnectSettingsGroup {
  id: string;
  labelKey: string;
}

/** A page that goes deeper than a row can (the look designer, the search sources). */
export interface ConnectSettingsLink {
  labelKey: string;
  /** A route path of the shell (`/settings/general/design`). */
  to: string;
}

/** One setting: a scalar or a Drive folder reference at `path` in its config document. */
export interface ConnectSettingsField {
  /** The config key (`table`, `mail.display`). */
  key: string;
  /**
   * Where the value sits in the key's document: a dotted path (`zebra`,
   * `dock.edge`), or its segments when a segment holds a dot itself
   * (`['panes', 'calendar.workspace', 'aside_open']`). The first segment is
   * the unit a tenant or company locks.
   */
  path: string | readonly string[];
  control: ConnectSettingsControl;
  /** Access checked on selection and use, without granting that access. */
  folderPermission?: DriveFolderPermission;
  /** The options of a `choice`, a `select` or an `order`; the presets of a `keybinding`. */
  options?: readonly ConnectSettingsOption[];
  /** What a `switch` stores when it is no boolean (`table.zebra`: `on` / `off`). */
  values?: { on: ConnectSettingsValue; off: ConnectSettingsValue };
  /** Range and step of a `number`. */
  min?: number;
  max?: number;
  step?: number;
  /** A short unit after a number every language writes alike (`s`, `min`, `%`). */
  unit?: string;
  /**
   * i18n key of a unit that is a word (`Tage` / `days`) — instead of `unit`,
   * shown in the reader's language like the title.
   */
  unitKey?: string;
  /** Longest text of a `text`. */
  maxLength?: number;
  /**
   * What applies while no layer sets the field — what the app falls back to.
   * `null`: nothing (the option of no value; a table keeps its own density).
   */
  default: ConnectSettingsValue | null;
  /** i18n key of the title. */
  labelKey: string;
  /** i18n key of what the setting does: short, plain words — mandatory. */
  descriptionKey: string;
  /** Extra search words in any language (synonyms, older names); never shown. */
  keywords?: readonly string[];
  /** Rarely needed: folded under „Erweitert" at the end of its section. */
  advanced?: boolean;
  /** Only an administrator's layer (tenant, company) sets it, as a policy. */
  adminOnly?: boolean;
  /**
   * Only the installation's own layer sets it (the default tenant's,
   * `CONNECT_INSTALLATION_LAYER`): a setting of the whole installation that
   * master reads from that one layer, such as the LAN entries of the endpoint
   * list. Master refuses a write anywhere else, so no other scope lists it.
   * Meant together with `adminOnly`.
   */
  installationOnly?: boolean;
  /**
   * Not ready to be seen yet: the settings list leaves the field out; the
   * look designer lists it in its section „In Entwicklung“, where it is set
   * on a design. Its default still applies to everybody — a preview changes
   * nothing until a design picks another value.
   */
  preview?: boolean;
  /**
   * `false`: a tenant or company cannot lock it — its lock unit (the first
   * path segment) holds more than this setting, e.g. `shell.panes`, the pane
   * state of every app.
   */
  lockable?: boolean;
  /** Id of the section's group the field is listed under. */
  group?: string;
  /**
   * Fields of the same config key with the same `distinctGroup` never show
   * each other's current value as an option, and a `keybinding` refuses a
   * recorded combo another field of the group holds („Schon vergeben“). For
   * `choice`, `select` and `keybinding` only. The option of no value and the
   * field's own current value always stay.
   */
  distinctGroup?: string;
  /**
   * A `keybinding`'s check of a recorded combo in the kit's notation
   * (`'Mod+Shift+F'`): the i18n key (of the app's table) of the reason it may
   * not be taken, null when it may. Keep it pure — a declaration is read in
   * node-env code too, so it must not import the UI kit.
   */
  keybindingProblem?: (combo: string) => string | null;
  link?: ConnectSettingsLink;
}

/** One section of the settings list: one entry of its navigation. */
export interface ConnectSettingsSection {
  /** Unique across all apps (`appearance`, `tables`, `mail`); the list's URL names it. */
  id: string;
  labelKey: string;
  /** i18n key of a line under the section's title. */
  descriptionKey?: string;
  /**
   * i18n key of the app's own group in the settings hub the section is listed
   * under, like a topic's `sectionKey`; without one it lands in a group named
   * like the app.
   */
  sectionKey?: string;
  /** Extra search words of the hub entry (never shown). */
  keywords?: readonly string[];
  /** The section's sub-headings, in their order. */
  groups?: readonly ConnectSettingsGroup[];
  /** A page that goes deeper than the section's rows (the search's sources). */
  link?: ConnectSettingsLink;
  /**
   * Per-object overrides of the section's fields in `<key>.<field>.<object>`
   * (one table's own settings, `table.tables`, I-2510): the list says how many
   * objects the edited layer overrides (`countLabelKey`, with `{count}`) and
   * offers „Überall übernehmen", which removes them from that layer.
   */
  objectOverrides?: { key: string; field: string; countLabelKey: string };
  fields: readonly ConnectSettingsField[];
}

/**
 * A page of an app whose arrangement a design can carry (I-2510): the
 * look designer's „Seiten" lists every declared page — what the design sets
 * of it, its tables, reset, export and import — and opens it to arrange it
 * for the design. A page an app does not declare still shows there by its
 * config key.
 */
export interface ConnectSettingsPage {
  /** The config key of the page's arrangement (`erp.screen.sales_quotes_show`). */
  key: string;
  /** i18n key of the page's name. */
  labelKey: string;
  /** i18n key of the area it is listed under (the app's menu area). */
  groupKey?: string;
  /**
   * The config keys of the tables on the page (`erp.grid.sales_quote_lines`):
   * their columns, and their own look in `table.tables.<key>`.
   */
  grids?: readonly string[];
  /** A route path of the shell where the page is reached (`/erp/sales/quotes`). */
  to?: string;
}

/**
 * What a design holds of one page: its arrangement (`<app>.screen.<page>`),
 * its tables' columns (`<app>.grid.<table>`) and their own look
 * (`table.tables.<grid>`) — the content a page editor shows and changes.
 */
export interface ConnectSettingsPageContent {
  screen?: JsonObject;
  grids: Record<string, JsonObject>;
  tables: Record<string, JsonObject>;
}

/**
 * The props of an app's page editor (`ConnectAppModule.settingsPageEditor`):
 * the look designer shows it when a page of the app is opened and writes
 * what it emits (`update:content`, the page's whole content) onto the design.
 */
export interface ConnectSettingsPageEditorProps {
  /** The page's key (`erp.screen.sales_quotes_show`). */
  pageKey: string;
  /** The tables on the page (`ConnectSettingsPage.grids`). */
  grids: readonly string[];
  content: ConnectSettingsPageContent;
  /** Nothing may be changed (the design cannot be copied). */
  readOnly?: boolean;
}

/** One line of a stored document as the look designer's „Seiten" shows it. */
export interface ConnectSettingsDocumentLine {
  label: string;
  value: string;
}

/**
 * Describes a document of one of an app's keys — a page's arrangement, a
 * table's columns — as lines a person reads, in the app's language (`t` is
 * the app's table). Technical on purpose: ids may show. Null for a key it
 * does not describe; the designer shows the document's fields then.
 */
export type ConnectSettingsDocumentDescriber = (
  key: string,
  doc: JsonObject,
  t: (key: string, params?: Record<string, unknown>) => string,
) => ConnectSettingsDocumentLine[] | null;

/** The syntax of a config key (master's `accountconfig` key pattern). */
const KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;

/** The path of a field as segments. */
export function connectSettingsPath(
  field: Pick<ConnectSettingsField, "path">,
): string[] {
  return typeof field.path === "string"
    ? field.path.split(".").filter(Boolean)
    : [...field.path];
}

/** A field's id, unique across the list: `table:zebra`, `shell:panes/calendar.workspace/aside_open`. */
export function connectSettingsFieldId(
  field: Pick<ConnectSettingsField, "key" | "path">,
): string {
  return `${field.key}:${connectSettingsPath(field).join("/")}`;
}

/** The top-level field a path belongs to — the unit a tenant or company locks. */
export function connectSettingsLockField(path: readonly string[]): string {
  return path[0] ?? "";
}

/** The value at a path of a document; `undefined` where there is none. */
export function connectSettingsValueAt(
  doc: unknown,
  path: readonly string[],
): unknown {
  let node: unknown = doc;
  for (const segment of path) {
    if (!isJsonObject(node)) return undefined;
    node = node[segment];
  }
  return node;
}

function nested(path: readonly string[], value: unknown): JsonObject {
  let out: unknown = value;
  for (let i = path.length - 1; i >= 0; i -= 1) out = { [path[i]!]: out };
  return out as JsonObject;
}

/**
 * The JSON merge patch (RFC 7386) that sets `path` of a layer (`own`, its
 * document) to `value` — or, for `null`/`undefined`, removes it. A removal
 * that leaves an object of the layer empty removes that object too, so a
 * layer keeps no empty shells (`{dock: {edge}}` without the edge is
 * `{dock: null}`).
 */
export function connectSettingsFieldPatch(
  own: unknown,
  path: readonly string[],
  value: unknown,
): JsonObject {
  if (!path.length) return {};
  if (value !== null && value !== undefined)
    return nested(path, cloneJson(value));
  let depth = path.length - 1;
  while (depth > 0) {
    const parent = connectSettingsValueAt(own, path.slice(0, depth));
    if (!isJsonObject(parent)) break;
    const child = path[depth]!;
    const others = Object.keys(parent).some(
      (name) => name !== child && parent[name] !== undefined,
    );
    if (others) break;
    depth -= 1;
  }
  return nested(path.slice(0, depth + 1), null);
}

/**
 * What a field shows for a stored value: a `switch` with `values` shows
 * whether the stored value is its `on`, every other field the value as it is.
 */
export function connectSettingsShownValue(
  field: Pick<ConnectSettingsField, "control" | "values">,
  stored: unknown,
): unknown {
  if (field.control !== "switch") return stored;
  if (field.values) {
    if (stored === field.values.on) return true;
    if (stored === field.values.off) return false;
    return undefined;
  }
  return typeof stored === "boolean" ? stored : undefined;
}

/** What a field stores for a value the user picked (the reverse of `connectSettingsShownValue`). */
export function connectSettingsStoredValue(
  field: Pick<ConnectSettingsField, "control" | "values">,
  shown: unknown,
): unknown {
  if (field.control === "switch" && field.values && typeof shown === "boolean")
    return shown ? field.values.on : field.values.off;
  return shown;
}

/** The separator an `order` joins its option values with. */
const ORDER_SEPARATOR = ",";

/**
 * The stored order of an `order` field (the option values joined by `,`)
 * normalized against the declared option `values`, which are in their default
 * order: unknown and repeated parts are dropped, a value the stored order
 * lacks (an option added later) is inserted at its default position — right
 * after the nearest value that precedes it in `values` and is present, at the
 * front when none is. A stored value that is no string, or empty, gives
 * `values`.
 */
export function connectSettingsOrder(
  stored: unknown,
  values: readonly string[],
): string[] {
  if (typeof stored !== "string" || stored === "") return [...values];
  const known = new Set(values);
  const order: string[] = [];
  for (const part of stored.split(ORDER_SEPARATOR)) {
    if (known.has(part) && !order.includes(part)) order.push(part);
  }
  values.forEach((value, index) => {
    if (order.includes(value)) return;
    let after = -1;
    for (let before = index - 1; before >= 0 && after < 0; before -= 1)
      after = order.indexOf(values[before]!);
    order.splice(after + 1, 0, value);
  });
  return order;
}

/** The fields of a key, in the sections' order. */
export function connectSettingsFieldsOf(
  sections: readonly ConnectSettingsSection[],
  key: string,
): ConnectSettingsField[] {
  return sections.flatMap((section) =>
    section.fields.filter((field) => field.key === key),
  );
}

/** Every config key the sections' fields name, in their order. */
export function connectSettingsKeys(
  sections: readonly ConnectSettingsSection[],
): string[] {
  return [
    ...new Set(
      sections.flatMap((section) => section.fields.map((field) => field.key)),
    ),
  ];
}

/**
 * The document a key holds while no layer sets anything: every declared
 * field's default at its path (a `null` default — no value — is left out).
 * Merged under master's answer it is what the app reads
 * (`useDeclaredAccountConfig(sections, key)`).
 */
export function connectSettingsDefaults(
  sections: readonly ConnectSettingsSection[],
  key: string,
): JsonObject {
  let doc: JsonObject = {};
  for (const field of connectSettingsFieldsOf(sections, key)) {
    if (field.default === null || field.default === undefined) continue;
    const path = connectSettingsPath(field);
    if (!path.length) continue;
    doc = mergeInto(doc, nested(path, field.default));
  }
  return doc;
}

function mergeInto(base: JsonObject, over: JsonObject): JsonObject {
  const out: JsonObject = { ...base };
  for (const [name, value] of Object.entries(over)) {
    const current = out[name];
    out[name] =
      isJsonObject(current) && isJsonObject(value)
        ? mergeInto(current, value)
        : value;
  }
  return out;
}

/* ── The guard every app's declaration runs through (vitest) ─────────────── */

/** A message table per language, as the app ships it (`{ de, en }`). */
export type ConnectSettingsMessageTables = Readonly<
  Record<string, Readonly<Record<string, unknown>>>
>;

export interface ConnectSettingsCheckOptions {
  /** The languages every text must exist in (default: `de` and `en`). */
  languages?: readonly string[];
  /** The keys the declaring app may name (default: any valid key). */
  allowKey?: (key: string) => boolean;
}

function lookup(table: unknown, key: string): unknown {
  let node: unknown = table;
  for (const part of key.split(".")) {
    if (!isJsonObject(node)) return undefined;
    node = node[part];
  }
  return node;
}

function text(table: unknown, key: string | undefined): string {
  if (!key) return "";
  const value = lookup(table, key);
  return typeof value === "string" ? value.trim() : "";
}

/** Whether `value` is a valid default of `field` (see `ConnectSettingsField.default`). */
export function connectSettingsDefaultIsValid(
  field: ConnectSettingsField,
): boolean {
  const value = field.default;
  switch (field.control) {
    case "switch":
      return field.values
        ? value === field.values.on || value === field.values.off
        : typeof value === "boolean";
    case "choice":
    case "select":
      return (field.options ?? []).some((option) => option.value === value);
    case "keybinding":
      return typeof value === "string";
    case "number":
      return (
        typeof value === "number" &&
        Number.isFinite(value) &&
        (field.min === undefined || value >= field.min) &&
        (field.max === undefined || value <= field.max)
      );
    case "text":
      return (
        value === null ||
        (typeof value === "string" &&
          (!field.maxLength || value.length <= field.maxLength))
      );
    case "appearance":
      return typeof value === "string" && value.trim() !== "";
    case "order": {
      if (typeof value !== "string") return false;
      const values = (field.options ?? []).map((option) => option.value);
      const parts = value.split(ORDER_SEPARATOR);
      return (
        parts.length === values.length &&
        new Set(parts).size === parts.length &&
        parts.every((part) => values.includes(part))
      );
    }
    case "drive-folder":
      // Stable identities belong to stored layers, never to code defaults.
      return value === null;
    default:
      return false;
  }
}

/**
 * Everything wrong with a declaration, one line per problem (empty: fine):
 * a field without a title or a description in every language (or a
 * description that only repeats the title), an option, group, section or
 * link without its caption, a `unitKey` without its text, a default that
 * is no valid value, a `choice` or `select`, `keybinding` or `order` without
 * options, an `order` option whose value is no string or holds a `,`, a
 * `keybinding` default the global search (⌘K, Ctrl+K) or the field's own
 * `keybindingProblem` refuses, an unknown group, a `distinctGroup` on another
 * control or one that only one field of its key uses, a repeated section or
 * field, a key of another syntax or one the app may not name.
 */
export function connectSettingsProblems(
  sections: readonly ConnectSettingsSection[],
  tables: ConnectSettingsMessageTables,
  options: ConnectSettingsCheckOptions = {},
): string[] {
  const languages = options.languages ?? ["de", "en"];
  const problems: string[] = [];
  const sectionIds = new Set<string>();
  const fieldIds = new Set<string>();
  /** Field locations of one `distinctGroup` of one key (`key` + group). */
  const distinctGroups = new Map<string, string[]>();

  const needText = (where: string, key: string | undefined) => {
    for (const lang of languages) {
      if (!text(tables[lang], key))
        problems.push(`${where}: no ${lang} text for ${key ?? "(no key)"}`);
    }
  };

  for (const section of sections) {
    const where = `section ${section.id}`;
    if (!section.id) problems.push("a section without an id");
    if (sectionIds.has(section.id)) problems.push(`${where}: declared twice`);
    sectionIds.add(section.id);
    needText(where, section.labelKey);
    if (section.descriptionKey) needText(where, section.descriptionKey);
    if (section.link) needText(`${where}, link`, section.link.labelKey);
    const groups = new Set<string>();
    for (const group of section.groups ?? []) {
      groups.add(group.id);
      needText(`${where}, group ${group.id}`, group.labelKey);
    }
    if (!section.fields.length) problems.push(`${where}: no fields`);
    for (const field of section.fields) {
      const id = connectSettingsFieldId(field);
      const at = `${where}, field ${id}`;
      if (fieldIds.has(id)) problems.push(`${at}: declared twice`);
      fieldIds.add(id);
      if (!KEY_PATTERN.test(field.key) || field.key.length > 128)
        problems.push(`${at}: ${field.key} is no config key`);
      else if (options.allowKey && !options.allowKey(field.key))
        problems.push(`${at}: the app may not declare the key ${field.key}`);
      if (!connectSettingsPath(field).length)
        problems.push(`${at}: an empty path`);
      if (field.control === "drive-folder") {
        if (!/^[a-z][a-z0-9_]*\.folders$/.test(field.key)) problems.push(`${at}: Drive folders belong to <module>.folders`);
        if (connectSettingsPath(field).length !== 1) problems.push(`${at}: a folder function is one top-level field`);
        if (field.folderPermission !== 'read' && field.folderPermission !== 'write') problems.push(`${at}: a folder function needs its read/write permission`);
      }
      needText(at, field.labelKey);
      needText(at, field.descriptionKey);
      for (const lang of languages) {
        const label = text(tables[lang], field.labelKey);
        const description = text(tables[lang], field.descriptionKey);
        if (label && description && label === description)
          problems.push(`${at}: the ${lang} description repeats the title`);
      }
      if (
        (field.control === "choice" ||
          field.control === "select" ||
          field.control === "keybinding" ||
          field.control === "order") &&
        !field.options?.length
      )
        problems.push(`${at}: a ${field.control} without options`);
      if (field.control === "order") {
        for (const option of field.options ?? []) {
          if (
            typeof option.value !== "string" ||
            option.value === "" ||
            option.value.includes(ORDER_SEPARATOR)
          )
            problems.push(
              `${at}: option ${String(option.value)} of an order is no string without "${ORDER_SEPARATOR}"`,
            );
        }
      }
      for (const option of field.options ?? []) {
        if (!option.label?.trim())
          needText(`${at}, option ${String(option.value)}`, option.labelKey);
      }
      if (field.link) needText(`${at}, link`, field.link.labelKey);
      if (field.unitKey) needText(`${at}, unit`, field.unitKey);
      if (field.group && !groups.has(field.group))
        problems.push(`${at}: unknown group ${field.group}`);
      if (field.distinctGroup) {
        if (
          field.control !== "choice" &&
          field.control !== "select" &&
          field.control !== "keybinding"
        ) {
          problems.push(
            `${at}: distinctGroup applies to a choice, a select or a keybinding, not a ${field.control}`,
          );
        }
        const bucket = `${field.key}\0${field.distinctGroup}`;
        const members = distinctGroups.get(bucket);
        if (members) members.push(at);
        else distinctGroups.set(bucket, [at]);
      }
      if (!connectSettingsDefaultIsValid(field))
        problems.push(
          `${at}: the default ${JSON.stringify(field.default)} is no valid value`,
        );
      else if (
        field.control === "keybinding" &&
        typeof field.default === "string" &&
        field.default !== ""
      ) {
        // The global search is refused for every keybinding, then the field's own check.
        if (connectComboIsGlobalSearch(field.default))
          problems.push(`${at}: the default ${field.default} is the global search`);
        else {
          const refused = field.keybindingProblem?.(field.default);
          if (refused)
            problems.push(
              `${at}: the default ${field.default} is refused (${refused})`,
            );
        }
      }
    }
  }
  for (const [bucket, members] of distinctGroups) {
    if (members.length > 1) continue;
    const group = bucket.slice(bucket.indexOf("\0") + 1);
    problems.push(
      `${members[0]}: distinct group ${group} holds only this field`,
    );
  }
  return problems;
}

/**
 * Everything wrong with an app's pages (`ConnectAppModule.settingsPages`), one
 * line per problem (empty: fine): a page or a table key of another syntax or
 * one the app may not name, a page declared twice, a name or an area without
 * its text in every language, a route that is no shell path.
 */
export function connectSettingsPageProblems(
  pages: readonly ConnectSettingsPage[],
  tables: ConnectSettingsMessageTables,
  options: ConnectSettingsCheckOptions = {},
): string[] {
  const languages = options.languages ?? ["de", "en"];
  const problems: string[] = [];
  const keys = new Set<string>();
  const validKey = (key: string) => KEY_PATTERN.test(key) && key.length <= 128;
  for (const page of pages) {
    const where = `page ${page.key}`;
    if (!validKey(page.key)) problems.push(`${where}: no config key`);
    else if (options.allowKey && !options.allowKey(page.key))
      problems.push(`${where}: the app may not declare the key`);
    if (keys.has(page.key)) problems.push(`${where}: declared twice`);
    keys.add(page.key);
    for (const key of [
      page.labelKey,
      ...(page.groupKey ? [page.groupKey] : []),
    ]) {
      for (const lang of languages) {
        if (!text(tables[lang], key))
          problems.push(`${where}: no ${lang} text for ${key}`);
      }
    }
    for (const grid of page.grids ?? []) {
      if (!validKey(grid))
        problems.push(`${where}, table ${grid}: no config key`);
      else if (options.allowKey && !options.allowKey(grid))
        problems.push(
          `${where}, table ${grid}: the app may not declare the key`,
        );
    }
    if (page.to !== undefined && !page.to.startsWith("/"))
      problems.push(`${where}: the route ${page.to} is no shell path`);
  }
  return problems;
}

/**
 * The layer of the installation-wide settings (`installationOnly`): the
 * default tenant's. Master's `accountconfig.InstallationSubject`.
 */
export const CONNECT_INSTALLATION_LAYER = {
  subjectType: "tenant",
  subjectId: 1,
} as const;
